import { useState } from 'react'
import { HandCoins, PiggyBank, Undo2 } from 'lucide-react'
import { formatPrice } from '../../lib/format'
import type { GroupPool } from '../../lib/types'

import { Avatar, Button, Card, Dialog, Input } from '../ui'
import { formatBalance, parseDollarsToCents } from '../wallet/money'

type PoolAction = 'contribute' | 'withdraw'

interface PoolPanelProps {
  pool: GroupPool
  /** The viewer's wallet balance — the client-side chip-in cap. */
  walletBalanceCents: number
  /** Resolve on success (dialog closes); reject to keep it open with `error`. */
  onContribute: (amountCents: number) => Promise<void> | void
  onWithdraw: (amountCents: number) => Promise<void> | void
  /** Server-side mutation state from the parent hook. */
  busy?: boolean
  error?: string | null
  /** Navigate to /wallet when the wallet can't cover a chip-in. */
  onGoToWallet?: () => void
  className?: string
}

/**
 * The group money pool: balance headline, net contribution per member, and
 * chip-in / take-back dialogs. Caps mirror the server (chip-in ≤ wallet
 * balance, withdraw ≤ my_withdrawable_cents) — the server stays authoritative.
 */
export function PoolPanel({
  pool,
  walletBalanceCents,
  onContribute,
  onWithdraw,
  busy = false,
  error = null,
  onGoToWallet,
  className,
}: PoolPanelProps) {
  const [action, setAction] = useState<PoolAction | null>(null)
  const [amountText, setAmountText] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const cents = parseDollarsToCents(amountText)
  const cap = action === 'withdraw' ? pool.my_withdrawable_cents : walletBalanceCents
  const insufficientWallet = action === 'contribute' && cents !== null && cents > walletBalanceCents
  const overWithdrawable = action === 'withdraw' && cents !== null && cents > pool.my_withdrawable_cents

  const amountError =
    amountText !== '' && cents === null
      ? 'Enter an amount in dollars, like 5.00.'
      : insufficientWallet
        ? 'That’s more than your wallet balance.'
        : overWithdrawable
          ? `You can take back up to ${formatPrice({ cents: pool.my_withdrawable_cents, currency: pool.currency })}.`
          : ''

  const valid = cents !== null && cents > 0 && !insufficientWallet && !overWithdrawable

  const closeDialog = () => {
    setAction(null)
    setAmountText('')
    setSubmitting(false)
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!valid || cents === null || busy || submitting) return
    setSubmitting(true)
    try {
      await (action === 'withdraw' ? onWithdraw(cents) : onContribute(cents))
      closeDialog()
    } catch {
      // The parent surfaces the server error via the `error` prop; keep the
      // dialog open so the member can adjust and retry.
      setSubmitting(false)
    }
  }

  return (
    <Card className={className}>
      <div className="flex items-center gap-2">
        <PiggyBank className="h-5 w-5 text-ember-300" strokeWidth={1.75} aria-hidden="true" />
        <h2 className="text-xl font-semibold text-text-strong">Group pool</h2>
      </div>
      <p className="mt-2 font-display text-3xl tracking-tight text-text-strong">
        {formatBalance(pool.balance_cents, pool.currency)}
      </p>

      {pool.contributions.length === 0 ? (
        <p className="mt-3 text-sm text-text-muted">No one&rsquo;s chipped in yet.</p>
      ) : (
        <ul className="mt-4 space-y-2.5">
          {pool.contributions.map((c) => (
            <li key={c.user_id} className="flex items-center gap-3">
              <Avatar name={c.display_name} src={c.avatar_url} size="sm" />
              <span className="min-w-0 flex-1 truncate text-sm text-text">
                {c.display_name ?? 'Member'}
              </span>
              <span className="shrink-0 text-sm tabular-nums text-text-muted">
                {formatPrice({ cents: c.amount_cents, currency: pool.currency })}
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-5 flex flex-wrap gap-2 border-t border-border pt-4">
        <Button size="sm" onClick={() => setAction('contribute')}>
          <HandCoins className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
          Chip in
        </Button>
        {pool.my_withdrawable_cents > 0 && (
          <Button size="sm" variant="secondary" onClick={() => setAction('withdraw')}>
            <Undo2 className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
            Withdraw
          </Button>
        )}
      </div>

      <Dialog
        open={action !== null}
        onClose={closeDialog}
        title={action === 'withdraw' ? 'Take money back' : 'Chip in to the pool'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label={action === 'withdraw' ? 'Amount to take back' : 'Amount to chip in'}
            inputMode="decimal"
            placeholder="5.00"
            value={amountText}
            onChange={(e) => setAmountText(e.target.value)}
            helpText={
              action === 'withdraw'
                ? `You can take back up to ${formatPrice({ cents: cap, currency: pool.currency })}.`
                : `Up to ${formatBalance(cap, pool.currency)} from your wallet.`
            }
            error={amountError || undefined}
          />
          {insufficientWallet && onGoToWallet && (
            <button
              type="button"
              onClick={onGoToWallet}
              className="text-sm font-medium text-ember-300 transition hover:text-ember-400"
            >
              Top up your wallet →
            </button>
          )}
          {error && <p className="text-sm text-danger-fg">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={closeDialog}>
              Cancel
            </Button>
            <Button type="submit" loading={busy || submitting} disabled={!valid || busy || submitting}>
              {action === 'withdraw' ? 'Withdraw' : 'Chip in'}
            </Button>
          </div>
        </form>
      </Dialog>
    </Card>
  )
}
